import React from 'react';
import { Box } from '@styleseat/ui';
import { BrandColor, FeatureTheme } from '@styleseat/brand';
import {
  Absolute,
  Stack,
  BodyText,
  HeaderText,
  BullettedListItem,
} from '../../../ui/index';
import PortalOverlay, { PortalOverlayModal } from '../../../ui/PortalOverlay';
import IncentivesCloseIcon from '../IncentivesCloseIcon';
import { ShoppingTagIcon } from '../../../ui/Icons/ShoppingTagIcon';
import { useResponsiveEffect } from '../../../../hooks';
import Currency from '../../../../modules/Currency';
import { DiscountDetails } from '../../../../store/NCDAndDiscountedPros.types';

export type AboutDiscountsModalProps = {
  onDismiss: () => void;
  discountDetails: DiscountDetails;
  renderCTA: () => React.ReactNode | React.ReactNode[];
};

export default function AboutDiscountsModal({
  onDismiss,
  renderCTA,
  discountDetails,
}: AboutDiscountsModalProps) {
  const is = useResponsiveEffect();
  const outerPaddingTop = (is.mobile || is.tablet) ? 48 : 0;
  const align = (is.mobile || is.tablet) ? 'left' : 'center';
  const alignItems = (is.mobile || is.tablet) ? 'flex-start' : 'center';
  const innerSpace = (is.mobile || is.tablet) ? 20 : 16;
  const innerMaxWidth = (is.mobile || is.tablet) ? null : 279;
  const amountOffPaddingTop = (is.mobile || is.tablet) ? 32 : 24;
  const flagLeft = (is.mobile || is.tablet) ? 212 : 245;
  const formattedMaxDiscount = discountDetails.discountMax && !discountDetails.discountAmount
    && Currency.format(discountDetails.discountMax);
  const amountOffText = (discountDetails.discountPercent) ? (`${discountDetails.discountPercent}%`) : (`$${discountDetails.discountAmount}`);
  return (
    <PortalOverlay
      onBackdropClick={onDismiss}
      name="about-incentives-modal"
    >
      <PortalOverlayModal
        position="fullScreen"
        color={FeatureTheme.DiscountsBackgroundColor}
      >
        <Stack
          minHeight="100%"
          maxHeight="100%"
          justifyContent="space-between"
        >
          <Stack>
            <Box
              minHeight={53}
            >
              <Absolute
                top={12}
                right={12}
              >
                <IncentivesCloseIcon
                  onDismiss={onDismiss}
                  size={16}
                  testID="about-incentives-modal_close-icon"
                />
              </Absolute>
            </Box>
            <Box
              paddingLeft={32}
              paddingRight={40}
              alignItems={alignItems}
              paddingTop={outerPaddingTop}
            >
              <Stack>
                <HeaderText
                  textAlign={align}
                  fontSize={48}
                  lineHeight={55}
                >
                  StyleSeat
                </HeaderText>
                <HeaderText
                  textAlign={align}
                  lineHeight={55}
                  fontSize={48}
                  color={FeatureTheme.DiscountsColor}
                >
                  Promotions
                </HeaderText>
                <Absolute
                  top={-20}
                  left={flagLeft}
                >
                  <ShoppingTagIcon
                    color={FeatureTheme.DiscountsColor}
                    height={36}
                    width={36}
                    transform="rotate(-25deg)"
                  />
                </Absolute>

                <Box paddingTop={amountOffPaddingTop}>
                  <HeaderText
                    color={BrandColor.Metal}
                    textAlign={align}
                    style={{ fontSize: 24 }}
                  >
                    {amountOffText}
                    &nbsp;off appointments
                  </HeaderText>
                </Box>
                <Stack
                  paddingTop={12}
                  paddingLeft={0}
                  space={innerSpace}
                  maxWidth={innerMaxWidth}
                >
                  <BullettedListItem color={BrandColor.Metal}>
                    <BodyText color={BrandColor.Metal}>
                      Get {amountOffText} off when you book with an eligible
                      new stylist on StyleSeat
                    </BodyText>
                  </BullettedListItem>
                  {formattedMaxDiscount && (
                    <BullettedListItem color={BrandColor.Metal}>
                      <BodyText color={BrandColor.Metal}>
                        *Max discount {formattedMaxDiscount}
                      </BodyText>
                    </BullettedListItem>
                  )}
                  <BullettedListItem color={BrandColor.Metal}>
                    <BodyText color={BrandColor.Metal}>
                      In order to get the deal, you must pay your stylist through StyleSeat
                    </BodyText>
                  </BullettedListItem>
                  <BullettedListItem color={BrandColor.Metal}>
                    <BodyText color={BrandColor.Metal}>
                      Look for the promotions icon to book and get your discount
                    </BodyText>
                  </BullettedListItem>
                </Stack>
              </Stack>
            </Box>
          </Stack>
          <Stack
            minHeight={88}
            borderTopWidth={1}
            borderTopColor={BrandColor.LightGrey}
          >
            {renderCTA()}
          </Stack>
        </Stack>
      </PortalOverlayModal>
    </PortalOverlay>
  );
}
